import React from 'react';

import { Close, PersonOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { AvatarGroupHorizontal, BaseButton, Box, LinkButton, Typography } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';
import { AriaTypes } from '@/modules/goals/types';
import UserMenu from '@/modules/tasks/features/actionItems/components/UserMenu';
import { Maybe, UserProfile, UserType } from '@/types/schema';

import StaticAssignMenuPopover from '../../features/actionItems/components/StaticAssignMenu/StaticAssignMenuPopover';
import {
  baseButtonFocusStyles,
  TASK_PILL_VARIANT_CONFIG,
  TaskPillVariant,
} from '../TaskStatus/styles';

interface Props extends AriaTypes {
  editable?: boolean;
  placeholder: string;
  className?: string;
  variant: TaskPillVariant;
  users?: Maybe<Pick<UserProfile, 'id' | 'name' | 'profilePicture'>[]>;
  onChange?: (userIds: string[]) => void;
  participants?: UserType[];
  showClearIcon?: boolean;
}

const TaskAssignee = (props: Props) => {
  const {
    className,
    variant,
    editable,
    users,
    placeholder,
    onChange,
    participants,
    showClearIcon,
    ...rest
  } = props;
  const config = TASK_PILL_VARIANT_CONFIG[variant];

  const onDelete = React.useCallback(() => {
    onChange?.([]);
  }, [onChange]);

  const renderContent = (containerClassName?: string) => {
    let label = placeholder;
    let icon = <PersonOutline className="h-20 w-20" fillColorToken={config.icon.fillColorToken} />;

    if (users?.length) {
      label = users.map((user) => user.name).join(', ');
      const avatars = users.map((user) => ({
        name: user.name,
        src: user.profilePicture,
      }));
      icon = <AvatarGroupHorizontal avatars={avatars} maxCount={3} size="xx-small" />;
    }

    return (
      <BaseButton
        aria-label="assignee"
        className={cn(
          baseButtonFocusStyles,
          containerClassName,
          'group space-s-8 max-w-[200px]',
          className,
          config.root.className,
          editable ? config.rootEditable.className : '',
        )}
        disabled={!editable}
        {...rest}>
        {icon}
        <Typography
          className={cn('truncate', config.label.className)}
          color={config.label.color}
          variant="body-short-02">
          {label}
        </Typography>
        {!showClearIcon || !users?.length ? null : (
          <Box onClick={stopPropagation}>
            <LinkButton
              className="h-16 w-16 opacity-0 group-hover:opacity-100"
              color={'internal'}
              onClick={onDelete}>
              <Close className="!h-16 !w-16" fillColorToken={config.icon.fillColorToken} />
            </LinkButton>
          </Box>
        )}
      </BaseButton>
    );
  };

  if (editable && onChange) {
    if (participants?.length) {
      return (
        <StaticAssignMenuPopover
          participants={participants}
          selectedUsers={users?.map((user) => user as UserType) || []}
          onClick={(id: string) => onChange?.([id])}>
          {renderContent('flex-1')}
        </StaticAssignMenuPopover>
      );
    }

    return (
      <UserMenu
        position={config.popoverPosition}
        selectedUserIds={users?.map((user) => user.id) || []}
        onChange={onChange}>
        {renderContent()}
      </UserMenu>
    );
  }

  return renderContent();
};

export default TaskAssignee;
